import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  lotInputSchema,
  LOT_STATUS_LABELS,
  TRANSPORT_KIND_LABELS,
  TRANSPORT_KINDS,
  vatLabel,
  type CompanyOption,
  type LotDetail,
  type ProjectOption,
  type TransportKind,
  type VehicleTypeOption,
} from "@omts/shared";
import { api, fieldErrorsOf, type LotInputBody } from "../api";
import { APP_TZ, isoToLocalInput, localInputToIso } from "../time";

/** Состояние формы: всё строками, как в инпутах; в число/дату приводится при отправке. */
type FormState = {
  projectId: string;
  transportKind: TransportKind;
  loadingAddress: string;
  loadingAt: string;
  intermediateAddresses: string[];
  unloadingAddress: string;
  vehicleTypeId: string;
  weightKg: string;
  lengthM: string;
  widthM: string;
  heightM: string;
  placesCount: string;
  cargoName: string;
  cargoDescription: string;
  specialConditions: string;
  insurance: string;
  deadlineAt: string;
  autoExtend: boolean;
};

const EMPTY: FormState = {
  projectId: "",
  transportKind: "domestic",
  loadingAddress: "",
  loadingAt: "",
  intermediateAddresses: [],
  unloadingAddress: "",
  vehicleTypeId: "",
  weightKg: "",
  lengthM: "",
  widthM: "",
  heightM: "",
  placesCount: "",
  cargoName: "",
  cargoDescription: "",
  specialConditions: "",
  insurance: "",
  deadlineAt: "",
  autoExtend: false,
};

type Errors = Partial<Record<keyof FormState | "form", string>>;

const num = (s: string): number | undefined => {
  const t = s.trim().replace(",", ".");
  return t === "" ? undefined : Number(t);
};
/** Пустая обязательная строка → undefined: zod скажет «обязательное поле», а не «не короче N символов». */
const req = (s: string): string | undefined => s.trim() || undefined;

/** Собирает тело запроса из строк формы. Невалидные значения оставляем zod'у — он подсветит поле. */
function toPayload(f: FormState): Record<string, unknown> {
  return {
    projectId: f.projectId || undefined,
    transportKind: f.transportKind,
    loadingAddress: req(f.loadingAddress),
    loadingAt: localInputToIso(f.loadingAt) ?? undefined,
    intermediateAddresses: f.intermediateAddresses.map((a) => a.trim()).filter(Boolean),
    unloadingAddress: req(f.unloadingAddress),
    vehicleTypeId: f.vehicleTypeId || undefined,
    weightKg: num(f.weightKg),
    lengthM: num(f.lengthM),
    widthM: num(f.widthM),
    heightM: num(f.heightM),
    placesCount: f.placesCount.trim() === "" ? null : num(f.placesCount),
    cargoName: req(f.cargoName),
    cargoDescription: f.cargoDescription.trim(),
    specialConditions: f.specialConditions.trim(),
    insurance: f.insurance.trim(),
    contactUserId: null,
    deadlineAt: localInputToIso(f.deadlineAt) ?? undefined,
    autoExtend: f.autoExtend,
  };
}

type ParseFailure = Extract<ReturnType<typeof lotInputSchema.safeParse>, { success: false }>;
type Issue = ParseFailure["error"]["issues"][number];

/** Сообщения zod по-русски: пользователю — по полям формы, а не «Required». */
function issueText(issue: Issue): string {
  switch (issue.code) {
    case "invalid_type":
      return issue.received === "undefined" || issue.received === "nan" || issue.received === "null"
        ? "Обязательное поле"
        : "Некорректное значение";
    case "invalid_date":
      return "Укажите дату и время";
    case "invalid_string":
      return issue.validation === "uuid" ? "Выберите значение из списка" : "Некорректное значение";
    case "invalid_enum_value":
      return "Выберите значение из списка";
    case "too_small":
      if (issue.type === "string") return issue.minimum === 1 ? "Обязательное поле" : `Не короче ${issue.minimum} символов`;
      return issue.inclusive ? `Не меньше ${issue.minimum}` : "Должно быть больше нуля";
    case "not_finite":
      return "Введите число";
    default:
      return issue.message;
  }
}

/** После рендера ошибок — прокрутить к первому подсвеченному полю. */
const scrollToFirstError = () =>
  requestAnimationFrame(() => document.querySelector(".invalid")?.scrollIntoView({ block: "center", behavior: "smooth" }));

function errorsFromIssues(issues: Issue[]): Errors {
  const out: Errors = {};
  for (const i of issues) {
    const key = (i.path[0] ?? "form") as keyof Errors;
    if (!out[key]) out[key] = issueText(i);
  }
  return out;
}

/** Копия лота: все поля, кроме дедлайна (если он уже прошёл) и служебных. */
function fromLot(d: LotDetail): FormState {
  const l = d.lot;
  const deadlineFuture = new Date(l.deadlineAt).getTime() > Date.now();
  return {
    projectId: l.projectId,
    transportKind: l.transportKind,
    loadingAddress: l.loadingAddress,
    loadingAt: isoToLocalInput(l.loadingAt),
    intermediateAddresses: [...l.intermediateAddresses],
    unloadingAddress: l.unloadingAddress,
    vehicleTypeId: l.vehicleTypeId,
    weightKg: String(l.weightKg),
    lengthM: l.lengthM,
    widthM: l.widthM,
    heightM: l.heightM,
    placesCount: l.placesCount === null ? "" : String(l.placesCount),
    cargoName: l.cargoName,
    cargoDescription: l.cargoDescription,
    specialConditions: l.specialConditions,
    insurance: l.insurance,
    deadlineAt: deadlineFuture ? isoToLocalInput(l.deadlineAt) : "",
    autoExtend: l.autoExtend,
  };
}

function Field({
  label,
  error,
  hint,
  required,
  full,
  children,
}: {
  label: string;
  error?: string;
  hint?: ReactNode;
  required?: boolean;
  full?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`field${error ? " invalid" : ""}${full ? " full" : ""}`}>
      <span>
        {label}
        {required && <b className="req"> *</b>}
      </span>
      {children}
      {error ? <small className="field-error">{error}</small> : hint ? <small className="hint">{hint}</small> : null}
    </label>
  );
}

/** Редактируемый лот (?edit=<id>): номер и статус нужны для заголовка и набора кнопок. */
type Editing = { id: string; number: string; status: "draft" | "active" };

export function NewLotPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const copyFrom = params.get("copy");
  const editId = params.get("edit");

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState<"draft" | "publish" | null>(null);
  const [copyNumber, setCopyNumber] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  /** Лот нельзя редактировать (есть ставки / не тот статус) — форму не показываем. */
  const [editBlocked, setEditBlocked] = useState<string | null>(null);

  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeOption[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.projects(), api.vehicleTypes(), api.companies()])
      .then(([p, v, c]) => {
        setProjects(p);
        setVehicleTypes(v);
        setCompanies(c);
      })
      .catch((e: Error) => setLoadError(`Не удалось загрузить справочники: ${e.message}`));
  }, []);

  useEffect(() => {
    if (!copyFrom) return;
    api
      .lot(copyFrom)
      .then((d) => {
        setForm(fromLot(d));
        setCopyNumber(d.lot.number);
      })
      .catch((e: Error) => setLoadError(`Не удалось загрузить лот для копии: ${e.message}`));
  }, [copyFrom]);

  useEffect(() => {
    if (!editId) return;
    api
      .lot(editId)
      .then((d) => {
        const { status } = d.lot;
        if (status !== "draft" && status !== "active") {
          setEditBlocked(`Заявку ${d.lot.number} нельзя редактировать: ${LOT_STATUS_LABELS[status].toLowerCase()}.`);
          return;
        }
        if (d.bids.length > 0) {
          setEditBlocked(`По заявке ${d.lot.number} уже есть ставки — редактирование недоступно. Отмените лот и создайте копию.`);
          return;
        }
        setForm(fromLot(d));
        setEditing({ id: d.lot.id, number: d.lot.number, status });
      })
      .catch((e: Error) => setLoadError(`Не удалось загрузить лот для редактирования: ${e.message}`));
  }, [editId]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  const linked = useMemo(() => companies.filter((c) => c.telegramLinked).length, [companies]);
  const unlinked = companies.length - linked;

  const submit = async (publish: boolean) => {
    setErrors({});
    const parsed = lotInputSchema.safeParse(toPayload(form));
    if (!parsed.success) {
      setErrors(errorsFromIssues(parsed.error.issues));
      scrollToFirstError();
      return;
    }
    if ((publish || editing?.status === "active") && parsed.data.deadlineAt.getTime() <= Date.now()) {
      setErrors({ deadlineAt: publish ? "Для публикации дедлайн должен быть в будущем" : "Дедлайн активной заявки должен быть в будущем" });
      scrollToFirstError();
      return;
    }
    const body: LotInputBody = {
      ...parsed.data,
      loadingAt: parsed.data.loadingAt.toISOString(),
      deadlineAt: parsed.data.deadlineAt.toISOString(),
    };

    setBusy(publish ? "publish" : "draft");
    try {
      const lot = editing ? await api.updateLot(editing.id, body) : await api.createLot(body);
      if (publish) {
        try {
          await api.publishLot(lot.id);
        } catch (e) {
          const msg = e instanceof Error ? e.message : "неизвестная ошибка";
          navigate(`/lots/${lot.id}`, { state: { notice: `Черновик ${lot.number} сохранён, но опубликовать не удалось: ${msg}` } });
          return;
        }
      }
      navigate(`/lots/${lot.id}`);
    } catch (e) {
      setErrors({ ...fieldErrorsOf(e), form: e instanceof Error ? e.message : "Ошибка сохранения" });
      scrollToFirstError();
    } finally {
      setBusy(null);
    }
  };

  const dimsError = errors.lengthM ?? errors.widthM ?? errors.heightM;

  const title = editing
    ? `Редактирование ${editing.number}`
    : editId
      ? "Редактирование заявки"
      : copyNumber
        ? `Новая заявка — копия ${copyNumber}`
        : "Новая заявка";
  const backTo = editId ? { to: `/lots/${editId}`, label: "К заявке" } : { to: "/", label: "К списку" };

  if (editBlocked) {
    return (
      <>
        <div className="page-header">
          <h1>{title}</h1>
          <Link to={backTo.to} className="btn">
            {backTo.label}
          </Link>
        </div>
        <p className="error">{editBlocked}</p>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <h1>{title}</h1>
        <Link to={backTo.to} className="btn">
          {backTo.label}
        </Link>
      </div>

      {loadError && <p className="error">{loadError}</p>}
      {editing?.status === "active" && (
        <p className="notice warn">
          Заявка уже опубликована. После сохранения текст у перевозчиков обновится, новые получатели получат заявку в Telegram.
        </p>
      )}

      <form
        className="lot-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit(false);
        }}
      >
        <section className="card">
          <h3>Маршрут</h3>
          <div className="form-grid">
            <Field label="Проект" required error={errors.projectId}>
              <select value={form.projectId} onChange={(e) => set("projectId", e.target.value)}>
                <option value="">— выберите проект —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Тип перевозки"
              required
              error={errors.transportKind}
              hint={`Перевозчики увидят цену ${vatLabel(form.transportKind)}: по Казахстану — с НДС, международная — без НДС`}
            >
              <select value={form.transportKind} onChange={(e) => set("transportKind", e.target.value as TransportKind)}>
                {TRANSPORT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {TRANSPORT_KIND_LABELS[k]} ({vatLabel(k)})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Адрес погрузки" required error={errors.loadingAddress}>
              <input value={form.loadingAddress} onChange={(e) => set("loadingAddress", e.target.value)} placeholder="Город, адрес склада" />
            </Field>
            <Field label="Дата и время погрузки" required error={errors.loadingAt} hint={`Время ${APP_TZ}`}>
              <input type="datetime-local" value={form.loadingAt} onChange={(e) => set("loadingAt", e.target.value)} />
            </Field>

            <div className="field full">
              <span>Промежуточные адреса</span>
              {form.intermediateAddresses.map((addr, i) => (
                <div className="row" key={i}>
                  <input
                    value={addr}
                    placeholder={`Промежуточный адрес ${i + 1}`}
                    onChange={(e) =>
                      set(
                        "intermediateAddresses",
                        form.intermediateAddresses.map((a, j) => (j === i ? e.target.value : a)),
                      )
                    }
                  />
                  <button
                    type="button"
                    className="btn"
                    aria-label="Удалить адрес"
                    title="Удалить адрес"
                    onClick={() =>
                      set(
                        "intermediateAddresses",
                        form.intermediateAddresses.filter((_, j) => j !== i),
                      )
                    }
                  >
                    Удалить
                  </button>
                </div>
              ))}
              <div>
                <button type="button" className="btn" onClick={() => set("intermediateAddresses", [...form.intermediateAddresses, ""])}>
                  + Добавить адрес
                </button>
              </div>
              {errors.intermediateAddresses && <small className="field-error">{errors.intermediateAddresses}</small>}
            </div>

            <Field label="Адрес выгрузки" required error={errors.unloadingAddress} full>
              <input value={form.unloadingAddress} onChange={(e) => set("unloadingAddress", e.target.value)} placeholder="Город, адрес получателя" />
            </Field>
          </div>
        </section>

        <section className="card">
          <h3>Груз и транспорт</h3>
          <div className="form-grid">
            <Field label="Тип транспорта" required error={errors.vehicleTypeId}>
              <select value={form.vehicleTypeId} onChange={(e) => set("vehicleTypeId", e.target.value)}>
                <option value="">— выберите тип —</option>
                {vehicleTypes.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Вес груза, кг" required error={errors.weightKg}>
              <input type="number" inputMode="numeric" min={1} step={1} value={form.weightKg} onChange={(e) => set("weightKg", e.target.value)} />
            </Field>

            <div className={`field${dimsError ? " invalid" : ""}`}>
              <span>
                Габариты Д × Ш × В, м<b className="req"> *</b>
              </span>
              <div className="row dims">
                <input type="number" inputMode="decimal" min={0.01} step={0.01} placeholder="Длина" value={form.lengthM} onChange={(e) => set("lengthM", e.target.value)} />
                <span className="muted">×</span>
                <input type="number" inputMode="decimal" min={0.01} step={0.01} placeholder="Ширина" value={form.widthM} onChange={(e) => set("widthM", e.target.value)} />
                <span className="muted">×</span>
                <input type="number" inputMode="decimal" min={0.01} step={0.01} placeholder="Высота" value={form.heightM} onChange={(e) => set("heightM", e.target.value)} />
              </div>
              {dimsError && <small className="field-error">{dimsError}</small>}
            </div>
            <Field label="Количество грузовых мест" error={errors.placesCount} hint="Желательно, если известно">
              <input type="number" inputMode="numeric" min={1} step={1} value={form.placesCount} onChange={(e) => set("placesCount", e.target.value)} />
            </Field>

            <Field label="Наименование груза" required error={errors.cargoName} full>
              <input value={form.cargoName} onChange={(e) => set("cargoName", e.target.value)} placeholder="Например: кабельная продукция на барабанах" />
            </Field>
            <Field label="Описание груза" error={errors.cargoDescription} full>
              <textarea rows={3} value={form.cargoDescription} onChange={(e) => set("cargoDescription", e.target.value)} />
            </Field>
            <Field label="Особые условия перевозки" error={errors.specialConditions}>
              <textarea rows={3} value={form.specialConditions} onChange={(e) => set("specialConditions", e.target.value)} placeholder="Температурный режим, крепление, сопровождение…" />
            </Field>
            <Field label="Страхование" error={errors.insurance}>
              <textarea rows={3} value={form.insurance} onChange={(e) => set("insurance", e.target.value)} placeholder="Нужно ли страховать груз и на какую сумму" />
            </Field>
          </div>
        </section>

        <section className="card">
          <h3>Торги</h3>
          <div className="form-grid">
            <Field label="Окончание приёма ставок" required error={errors.deadlineAt} hint={`Часовой пояс ${APP_TZ}`}>
              <input type="datetime-local" value={form.deadlineAt} onChange={(e) => set("deadlineAt", e.target.value)} />
            </Field>
            <label className="field check">
              <span>Автопродление</span>
              <span className="row">
                <input type="checkbox" checked={form.autoExtend} onChange={(e) => set("autoExtend", e.target.checked)} />
                <span>Автопродление (30 мин, не более 2 раз)</span>
              </span>
              <small className="hint">Если ставка приходит менее чем за 30 минут до дедлайна, торги продлеваются на 30 минут</small>
            </label>
          </div>
        </section>

        <section className="card">
          <h3>Получатели</h3>
          <p>
            Заявка уходит <b>всем активным перевозчикам</b> из Справочников — выбор отдельных компаний не предусмотрен.
            Сейчас их {companies.length}: с Telegram — {linked}
            {unlinked > 0 && <>, без Telegram — {unlinked} (они не получат заявку, пока не перейдут по ссылке-приглашению)</>}.
          </p>
          {companies.length === 0 && !loadError && (
            <p className="error">Активных перевозчиков нет — опубликовать заявку не получится. Добавьте компании в Справочниках.</p>
          )}
        </section>

        {errors.form && <p className="error">{errors.form}</p>}

        <div className="form-actions">
          {editing?.status === "active" ? (
            <>
              <button type="submit" className="btn primary" disabled={busy !== null}>
                {busy === "draft" ? "Сохраняем…" : "Сохранить"}
              </button>
              <span className="muted">Уже разосланным получателям заявка повторно не отправляется — у них обновится текст</span>
            </>
          ) : (
            <>
              <button type="submit" className="btn" disabled={busy !== null}>
                {busy === "draft" ? "Сохраняем…" : editing ? "Сохранить" : "Сохранить черновик"}
              </button>
              <button type="button" className="btn primary" disabled={busy !== null} onClick={() => void submit(true)}>
                {busy === "publish" ? "Публикуем…" : editing ? "Сохранить и опубликовать" : "Опубликовать"}
              </button>
              <span className="muted">Публикация разошлёт заявку выбранным перевозчикам в Telegram</span>
            </>
          )}
        </div>
      </form>
    </>
  );
}
